import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Accessory as AccessoryPrimitive } from 'app/design/primitives/page/accessory';
import { Text } from 'app/design/primitives/text';
import { useNavigation } from 'app/hooks/navigation';
import type { Loan } from 'app/types/loan';
import { canUserCompleteLoan, canUserRateLoan, getCurrentUserLoanRating, LoanStatus } from 'app/types/loan';
import type { User } from 'app/types/user';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, View } from 'react-native';
import { getLoanInfos, getTranslationSuffix } from '../functions';

import {
  CreateOfferFromRequestModal,
  useCreateOfferFromRequestModal,
} from 'app/components/create-offer-from-request-modal';
import { LoanReturnConfirmation, useLoanReturnConfirmation } from 'app/components/loan-return-confirmation';
import { IconChevronRight } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { isDonationListing } from 'app/lib/listing';
import { navigateToListing } from 'app/screens/billboard/navigator-helper';
import { createTheme as createDSTheme } from 'app/shopify';
import { ListingSubtype, TransactionMode } from 'app/types/listing';
import type { TFunction } from 'i18next';
const theme = createDSTheme();

interface AccessorySectionProps {
  loan: Loan;
  user: User;
}

function getLoanRatingSubject(loan: Loan, isLender: boolean, fallbackName: string) {
  const otherUser = isLender ? loan.borrower : loan.lender;
  const otherUserName = [otherUser.profile?.firstName, otherUser.profile?.lastName].filter(Boolean).join(' ').trim();
  const name = otherUserName || fallbackName;

  return {
    otherUser,
    otherUserName: name,
    subjectImage: isLender
      ? (otherUser.profile?.picture ?? null)
      : (loan.listing.picture ?? loan.listing.pictures?.[0]?.url ?? null),
    subjectName: isLender ? name : loan.listing.item.name,
  };
}

function RatingThumbButtons({ onRate }: { onRate: (initialExperience: 'positive' | 'negative') => void }) {
  return (
    <Row gap="spacing.general.x2" style={{ width: '100%' }}>
      <Pressable
        onPress={() => onRate('positive')}
        style={{
          alignItems: 'center',
          borderColor: '#DDDACF',
          borderRadius: 39,
          borderWidth: 1,
          flex: 1,
          height: 48,
          justifyContent: 'center',
          paddingHorizontal: 14,
        }}
      >
        <Text fontSize={24} fontWeight="700">
          👍
        </Text>
      </Pressable>
      <Pressable
        onPress={() => onRate('negative')}
        style={{
          alignItems: 'center',
          borderColor: '#DDDACF',
          borderRadius: 39,
          borderWidth: 1,
          flex: 1,
          height: 48,
          justifyContent: 'center',
          paddingHorizontal: 14,
        }}
      >
        <Text fontSize={24} fontWeight="700">
          👎
        </Text>
      </Pressable>
    </Row>
  );
}

function RatingPromptBottomSheet({
  isOpen,
  name,
  onClose,
  onRate,
}: {
  isOpen: boolean;
  name: string;
  onClose: () => void;
  onRate: (initialExperience: 'positive' | 'negative') => void;
}) {
  const { t } = useTranslation('one-loan');

  return (
    <BottomSheet onClose={onClose} open={isOpen}>
      <Column
        alignItems="center"
        gap="spacing.general.x4"
        paddingHorizontal="spacing.general.x4"
        style={{ paddingBottom: 40, paddingTop: 24 }}
      >
        <Text fontSize={20} fontWeight="700" lineHeight={22} textAlign="center" color="black">
          {t('ratingPrompt.title', { name })}
        </Text>
        <Text color="text.secondary" fontSize={14} textAlign="center">
          {t('ratingPrompt.subtitle')}
        </Text>
        <RatingThumbButtons onRate={onRate} />
        <Pressable onPress={onClose}>
          <Text
            fontSize={14}
            fontWeight="700"
            lineHeight={16}
            textAlign="center"
            style={{ color: '#1F2635', textDecorationLine: 'underline' }}
          >
            {t('ratingPrompt.dismiss')}
          </Text>
        </Pressable>
      </Column>
    </BottomSheet>
  );
}

function RatingAccessory({
  hasRated,
  isRecommended,
  name,
  onPress,
}: {
  hasRated: boolean;
  isRecommended: boolean;
  name: string;
  onPress: () => void;
}) {
  const { t } = useTranslation('one-loan');

  return (
    <AccessoryPrimitive>
      <View
        style={{
          marginHorizontal: theme.spacing['spacing.general.x4'],
          marginTop: theme.spacing['spacing.general.x4'],
        }}
      >
        <Pressable
          onPress={onPress}
          style={
            hasRated
              ? {
                  alignItems: 'center',
                  borderColor: theme.colors['surface.disabled'],
                  borderRadius: 24,
                  borderWidth: 1,
                  flexDirection: 'row',
                  gap: 8,
                  justifyContent: 'center',
                  padding: 16,
                }
              : {
                  alignItems: 'center',
                  backgroundColor: '#1F2635',
                  borderRadius: 1000,
                  justifyContent: 'center',
                  padding: 16,
                }
          }
        >
          {hasRated ? (
            <>
              <Text fontSize={14} lineHeight={13.5} color="black" paddingLeft="spacing.general.x2">
                {t('ratingAccessory.mine')}
              </Text>
              <Text fontSize={14} lineHeight={13.5} color="black">
                {isRecommended ? '👍 ' : '👎 '}
                <Text
                  fontSize={14}
                  fontWeight="700"
                  lineHeight={13.5}
                  style={{
                    color: isRecommended ? theme.colors['text.emphasized'] : theme.colors['text.error'],
                  }}
                >
                  {t(isRecommended ? 'ratingAccessory.recommended' : 'ratingAccessory.notRecommended')}
                </Text>
              </Text>
              <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} size={24} color="#1F2635" />
            </>
          ) : (
            <Text color="surface.default" fontSize={16} fontWeight="500" lineHeight={18} textAlign="center">
              {t('ratingAccessory.rate', { name })}
            </Text>
          )}
        </Pressable>
      </View>
    </AccessoryPrimitive>
  );
}

function getCtaButtonText({
  ctaConfirmReturn,
  isDonation,
  isLender,
  isParticipations,
  isSell,
  isService,
  loan,
  t,
  tSuffix,
}: {
  ctaConfirmReturn: boolean;
  isDonation: boolean;
  isLender: boolean;
  isParticipations: boolean;
  isSell: boolean;
  isService: boolean;
  loan: Loan;
  t: TFunction<'one-loan'>;
  tSuffix: string;
}) {
  if (loan.status === LoanStatus.Rejected) return undefined;
  if (isParticipations) {
    return isLender ? t('viewParticipationButtonText', { ns: 'one-listing' }) : t('listParticipants');
  }
  if (ctaConfirmReturn) {
    const tKey = isSell
      ? 'sellDoneButtonText'
      : isService
        ? 'serviceDoneButtonText'
        : isDonation
          ? 'donationDoneButtonText'
          : 'itemReturnedButtonText';
    return t(tKey, { ns: 'one-listing' });
  }
  if (loan.status !== LoanStatus.Completed && loan.status !== LoanStatus.Returned) {
    return t(`changeDatesButtonText.${tSuffix}`);
  }
  return undefined;
}

export function Accessory({ loan: originalLoan, user }: AccessorySectionProps) {
  const { t } = useTranslation('one-loan');

  const navigation = useNavigation();
  const [isRatingPromptOpen, setIsRatingPromptOpen] = useState(false);

  const createOfferFromRequestModalProps = useCreateOfferFromRequestModal(originalLoan);
  const { setIsOpen: setIsCreateOfferModalOpen } = createOfferFromRequestModalProps;

  const loanReturnConfirmationProps = useLoanReturnConfirmation(
    originalLoan,
    () => setIsCreateOfferModalOpen(true),
    (completedLoan) => {
      if (canUserRateLoan(completedLoan, user.id)) {
        setIsRatingPromptOpen(true);
      }
    },
  );
  const { loan, setIsOpen: setIsReturnConfirmationModalOpen } = loanReturnConfirmationProps;

  const { loanType, ctaConfirmReturn, isLender } = (() => {
    const infos = getLoanInfos(loan, user);
    return {
      ...infos,
      ctaConfirmReturn: canUserCompleteLoan(loan, infos.isLender),
    };
  })();

  const isService = loan.listing.subtype === ListingSubtype.service;
  const isDonation = isDonationListing(loan.listing);
  const isSell = loan.listing.transactionMode === TransactionMode.sell;
  const isParticipations = Boolean(loan.listing.maxParticipants && loan.listing.maxParticipants > 0);
  const currentUserRating = getCurrentUserLoanRating(loan, user.id);
  const hasRated = typeof currentUserRating === 'number';
  const canRate = canUserRateLoan(loan, user.id);
  const isRecommended = typeof currentUserRating === 'number' && currentUserRating >= 3;
  const ratingSubject = getLoanRatingSubject(loan, isLender, t('deletedUser', { ns: 'common' }));

  const tSuffix = getTranslationSuffix({ isDonation, isSell, isService, loanType });

  const showDatesPicker = () => {
    navigation.navigate(
      'loan-dates-picker',
      {
        loanId: loan.id,
        url: loan.channelURL,
        promptIntroduceYourself: false,
        goBackTo: 'one-loan',
      },
      { pop: true },
    );
  };

  const ctaButtonText = getCtaButtonText({
    ctaConfirmReturn,
    isDonation,
    isLender,
    isParticipations,
    isSell,
    isService,
    loan,
    t,
    tSuffix,
  });
  const ctaOnPress = () =>
    isParticipations
      ? isLender
        ? navigateToListing(navigation, loan.listing.id)
        : navigation.navigate('loan-participants', { loanId: loan.id }, { pop: true })
      : ctaConfirmReturn
        ? setIsReturnConfirmationModalOpen(true)
        : showDatesPicker();

  const openRating = (initialExperience?: 'positive' | 'negative') => {
    setIsRatingPromptOpen(false);
    navigation.navigate(
      'new-rating',
      {
        loanId: loan.id,
        listingId: loan.listingId,
        channelUrl: loan.channelURL,
        isLender,
        subjectImage: ratingSubject.subjectImage,
        subjectName: ratingSubject.subjectName,
        initialExperience,
      },
      { pop: true },
    );
  };

  const openMyRating = () => {
    navigation.navigate(
      'ratings',
      {
        userId: ratingSubject.otherUser.id,
        subjectName: ratingSubject.otherUserName,
        scrollToLoanId: loan.id,
      },
      { pop: true },
    );
  };

  return (
    <>
      {ctaButtonText ? (
        <AccessoryPrimitive>
          <Row marginHorizontal="spacing.general.x4" marginTop="spacing.general.x4">
            <Button flex={1} onPress={ctaOnPress} variant="primary">
              <Text color="surface.default" fontWeight="700" textAlign="center">
                {ctaButtonText}
              </Text>
            </Button>
          </Row>
        </AccessoryPrimitive>
      ) : hasRated || canRate ? (
        <RatingAccessory
          hasRated={hasRated}
          isRecommended={isRecommended}
          name={ratingSubject.otherUser.profile?.firstName || ratingSubject.otherUserName}
          onPress={hasRated ? openMyRating : () => openRating()}
        />
      ) : null}

      <LoanReturnConfirmation {...loanReturnConfirmationProps} />
      <RatingPromptBottomSheet
        isOpen={isRatingPromptOpen}
        name={ratingSubject.otherUser.profile?.firstName || ratingSubject.otherUserName}
        onClose={() => setIsRatingPromptOpen(false)}
        onRate={openRating}
      />

      <CreateOfferFromRequestModal {...createOfferFromRequestModalProps} />
    </>
  );
}
